import React, { useMemo } from "react";
import { motion } from "framer-motion";
import OracleScoreRing from "@/components/animation/OracleScoreRing";
import AnimatedRiskMeter from "@/components/animation/AnimatedRiskMeter";
import ConfidencePulse from "@/components/animation/ConfidencePulse";
import { useReducedMotionSafe } from "@/components/animation/AnimationSettings";
import { PALETTE, BIAS_COLORS, COMPLIANCE_FOOTER_LINE, buildSparkline, biasColor, fmtNumber } from "@/components/animation/MotionShared";

/**
 * Animated Market Card — reusable cinematic card for any asset class.
 *
 * Renders:
 *   • symbol + bias pill (Buy / Hold / Watch / Avoid)
 *   • entry / stop / target ranges
 *   • ORACLE Score ring (0–100)
 *   • confidence pulse
 *   • animated risk bar
 *   • time horizon
 *   • animated sparkline
 *   • compliance footer
 */
function Sparkline({ symbol, height = 44, accent = PALETTE.greenAccent, testid }) {
  const reduce = useReducedMotionSafe();
  const data = useMemo(() => buildSparkline({ seed: `spark:${symbol}`, points: 48, base: 100, vol: 3 }), [symbol]);
  const w = 220;
  const min = Math.min(...data), max = Math.max(...data);
  const x = (i) => (i * (w - 6)) / (data.length - 1) + 3;
  const y = (v) => 3 + (height - 6) * (1 - (v - min) / (max - min || 1));
  const path = data.map((v, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(2)} ${y(v).toFixed(2)}`).join(" ");
  const areaPath = `${path} L ${x(data.length - 1).toFixed(2)} ${height - 3} L ${x(0).toFixed(2)} ${height - 3} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none" width="100%" height={height} data-testid={testid} role="img" aria-label={`${symbol} sparkline`}>
      <defs>
        <linearGradient id={`sp-${testid}-grad`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={accent} stopOpacity="0.45" />
          <stop offset="100%" stopColor={accent} stopOpacity="0" />
        </linearGradient>
      </defs>
      <motion.path d={areaPath} fill={`url(#sp-${testid}-grad)`}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={reduce ? { duration: 0 } : { duration: 0.6 }} />
      <motion.path d={path} fill="none" stroke={accent} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={reduce ? { duration: 0 } : { duration: 1.1, ease: "easeOut" }} />
    </svg>
  );
}

export default function AnimatedMarketCard({
  symbol = "AAPL",
  assetClass = "stocks",
  bias = "watch",         // buy | hold | watch | avoid
  entry = [180, 184],
  stop  = [176],
  target = [191, 198],
  score = 62,
  risk  = "medium",       // low | medium | high
  confidence = 64,
  horizon = "swing · 2–6d",
  notes,
  testid = "animated-market-card",
}) {
  const reduce = useReducedMotionSafe();
  const biasC = biasColor(bias);
  const fmtRange = (r) => Array.isArray(r) ? r.map((x) => fmtNumber(x, 2)).join(" – ") : fmtNumber(r, 2);
  return (
    <motion.article
      data-testid={testid}
      style={S.shell}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={reduce ? { duration: 0 } : { duration: 0.45, ease: "easeOut" }}
    >
      <header style={S.head}>
        <div style={S.titleRow}>
          <strong style={S.symbol}>{symbol}</strong>
          <span style={{ ...S.assetChip }}>{String(assetClass).toUpperCase()}</span>
        </div>
        <span style={{ ...S.biasPill, color: biasC, borderColor: biasC, background: `${biasC}1a` }} data-testid={`${testid}-bias`}>
          {String(bias).toUpperCase()}
        </span>
      </header>

      <div style={S.body}>
        <div style={S.left}>
          <OracleScoreRing score={score} size={92} testid={`${testid}-score-ring`} compact />
          <ConfidencePulse value={confidence} testid={`${testid}-confidence-pulse`} size={60} />
        </div>
        <div style={S.right}>
          <dl style={S.grid}>
            <div style={S.gridRow}><dt style={S.gridLbl}>Entry</dt><dd style={S.gridVal}>{fmtRange(entry)}</dd></div>
            <div style={S.gridRow}><dt style={S.gridLbl}>Stop</dt><dd style={S.gridVal}>{fmtRange(stop)}</dd></div>
            <div style={S.gridRow}><dt style={S.gridLbl}>Target</dt><dd style={S.gridVal}>{fmtRange(target)}</dd></div>
            <div style={S.gridRow}><dt style={S.gridLbl}>Horizon</dt><dd style={S.gridVal}>{horizon}</dd></div>
          </dl>
          <AnimatedRiskMeter level={risk} testid={`${testid}-risk-meter`} />
        </div>
      </div>

      <Sparkline symbol={symbol} accent={biasC === BIAS_COLORS.avoid ? PALETTE.red : PALETTE.greenAccent} testid={`${testid}-sparkline`} />

      {notes && <p style={S.notes}>{notes}</p>}
      <footer style={S.footer}>
        <span style={S.compliance}>{COMPLIANCE_FOOTER_LINE}</span>
      </footer>
    </motion.article>
  );
}

const S = {
  shell: { background: "linear-gradient(135deg, rgba(11,15,20,0.95), rgba(8,32,22,0.95))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 14, padding: 12, display: "flex", flexDirection: "column", gap: 10, color: PALETTE.slate3, boxShadow: "0 6px 22px rgba(0,0,0,0.45)", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },
  titleRow: { display: "flex", alignItems: "center", gap: 8 },
  symbol: { color: PALETTE.goldBright, fontSize: 17, letterSpacing: "0.04em" },
  assetChip: { color: PALETTE.gold, border: `1px solid ${PALETTE.goldGlow}`, padding: "2px 8px", borderRadius: 999, fontFamily: "'Space Mono', monospace", fontSize: 9.5, letterSpacing: "0.10em", fontWeight: 800, background: "rgba(197,160,89,0.06)" },
  biasPill: { fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800, letterSpacing: "0.10em", padding: "4px 11px", border: "1px solid", borderRadius: 999 },
  body: { display: "grid", gridTemplateColumns: "110px 1fr", gap: 10, alignItems: "center" },
  left: { display: "flex", flexDirection: "column", alignItems: "center", gap: 10 },
  right: { display: "flex", flexDirection: "column", gap: 8, minWidth: 0 },
  grid: { margin: 0, padding: 0, display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 4 },
  gridRow: { display: "flex", justifyContent: "space-between", padding: "4px 8px", background: "#040608", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 6, minWidth: 0 },
  gridLbl: { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.10em" },
  gridVal: { color: PALETTE.goldBright, fontFamily: "'Space Mono', monospace", fontSize: 11.5, fontWeight: 800, margin: 0 },
  notes: { color: PALETTE.slate3, fontSize: 12, lineHeight: 1.5, margin: 0 },
  footer: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  compliance: { color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.06em" },
};
